import React, { useState, useEffect } from 'react';
import { Shield, Plus, Edit2, Trash2, Save, X, AlertCircle } from 'lucide-react';

export default function CoherenceRulesSection({ rules, onChange }) {
  const [editingIndex, setEditingIndex] = useState(null);
  const [newRule, setNewRule] = useState({ title: '', rule: '', priority: 'high', category: 'accuracy' });
  const [isAdding, setIsAdding] = useState(false);

  const ruleCategories = [
    { value: 'accuracy', label: 'Précision & Exactitude', color: 'bg-red-100 text-red-800' },
    { value: 'consistency', label: 'Cohérence', color: 'bg-blue-100 text-blue-800' },
    { value: 'tone', label: 'Ton & Style', color: 'bg-purple-100 text-purple-800' },
    { value: 'safety', label: 'Sécurité & Conformité', color: 'bg-orange-100 text-orange-800' }
  ];

  const priorities = [
    { value: 'critical', label: 'Critique', icon: '🔴' },
    { value: 'high', label: 'Haute', icon: '🟠' },
    { value: 'medium', label: 'Moyenne', icon: '🟡' }
  ];

  const defaultRules = [
    {
      id: 1,
      title: 'Adhérence stricte à la base de connaissances',
      rule: 'Tu DOIS TOUJOURS utiliser les informations de la base de connaissances et des FAQs. NE JAMAIS inventer ou supposer des informations qui contredisent la base de connaissances. Si une question correspond à une FAQ, utilise EXACTEMENT la réponse de la FAQ.',
      priority: 'critical',
      category: 'accuracy'
    },
    {
      id: 2,
      title: 'Interdiction de contredire les services acceptés',
      rule: 'INTERDIT: Dire qu\'on n\'envoie pas quelque chose qui est listé dans les services acceptés ou la base de connaissances produits. Toujours vérifier la liste des produits acceptés avant de répondre négativement.',
      priority: 'critical',
      category: 'accuracy'
    },
    {
      id: 3,
      title: 'Gestion de l\'incertitude',
      rule: 'Si tu n\'es pas sûr d\'une information, propose de contacter le service client plutôt que de donner une réponse incorrecte. Utilise des phrases comme "Laisse-moi vérifier ça avec l\'équipe" ou "Pour être sûr, contacte-nous directement".',
      priority: 'high',
      category: 'accuracy'
    },
    {
      id: 4,
      title: 'Expressions neutres obligatoires',
      rule: 'N\'utilise JAMAIS "mon frère" ou "ma sœur" car tu ne connais pas le genre de ton interlocuteur. Utilise des expressions NEUTRES: "boss", "chef", "ami", "mon pote", "gars", "toi même tu sais".',
      priority: 'critical',
      category: 'tone'
    },
    {
      id: 5,
      title: 'Cohérence des tarifs et promotions',
      rule: 'Toujours utiliser les tarifs configurés dans la section Tarifs. Si des promotions sont actives, les mentionner systématiquement avec enthousiasme. Ne jamais inventer de prix ou de promotions.',
      priority: 'critical',
      category: 'consistency'
    },
    {
      id: 6,
      title: 'Cohérence des délais',
      rule: 'Utiliser uniquement les délais configurés (généralement 4-6 semaines). Ne pas promettre de délais plus courts sans vérification. Mentionner que ce sont des délais indicatifs.',
      priority: 'high',
      category: 'consistency'
    },
    {
      id: 7,
      title: 'Style familier et chaleureux',
      rule: 'Toujours maintenir un ton familier avec expressions ivoiriennes (nouchi). Utiliser des emojis pour être plus chaleureuse. Être encourageante et rassurante.',
      priority: 'medium',
      category: 'tone'
    },
    {
      id: 8,
      title: 'Vérification produits interdits',
      rule: 'Avant de confirmer qu\'un produit est accepté, vérifier qu\'il n\'est pas dans la liste des produits interdits. Si interdit, expliquer clairement pourquoi avec empathie.',
      priority: 'critical',
      category: 'safety'
    },
    {
      id: 9,
      title: 'Documents requis précis',
      rule: 'Toujours mentionner les documents requis spécifiques au type de produit (véhicules, électroménager, etc.). Ne pas généraliser. Utiliser la base de connaissances produits.',
      priority: 'high',
      category: 'accuracy'
    },
    {
      id: 10,
      title: 'Redirection appropriée',
      rule: 'Si la question nécessite une action sur le site (suivi, devis, inscription), fournir le lien exact de la page. Ne pas laisser le client chercher.',
      priority: 'medium',
      category: 'consistency'
    },
    {
      id: 11,
      title: 'Reconnaissance des synonymes barrique/carton',
      rule: 'CRITIQUE: Reconnaître que barrique = bargo = fût = futs = fûts = bidon = bidons. Tous ces mots désignent la même chose. Appliquer les mêmes règles de tarification et processus pour tous ces synonymes.',
      priority: 'critical',
      category: 'accuracy'
    },
    {
      id: 12,
      title: 'Distinction intention achat vs envoi (barrique/carton)',
      rule: 'RÈGLE ABSOLUE: Faire la différence entre 1) Client veut ACHETER une barrique/carton vide (livraison à domicile) et 2) Client veut ENVOYER une barrique/carton rempli (collecte à domicile). Si intention ambiguë, TOUJOURS clarifier avec: "Tu veux acheter une barrique vide qu\'on te livre, ou tu as déjà une barrique remplie à envoyer?"',
      priority: 'critical',
      category: 'accuracy'
    },
    {
      id: 13,
      title: 'Demande de localisation avant confirmation prix barrique/carton vide',
      rule: 'Pour achat de barrique/carton vide: TOUJOURS demander la localisation du client (ville et code postal) AVANT de confirmer le prix final. Dire: "Et tu es où exactement? (ville et code postal) 📍 C\'est pour confirmer le prix de livraison."',
      priority: 'critical',
      category: 'accuracy'
    },
    {
      id: 14,
      title: 'Clarification prix barrique: vide vs transport',
      rule: 'Quand client demande "combien coûte une barrique/un carton?" SANS contexte, TOUJOURS clarifier: "Tu parles du prix de la barrique vide qu\'on te livre, ou du prix du transport d\'une barrique remplie vers Abidjan? 🤔 C\'est deux choses différentes!" Ne JAMAIS donner un prix sans cette clarification.',
      priority: 'critical',
      category: 'accuracy'
    },
    {
      id: 15,
      title: 'Mention obligatoire promo avril 150€',
      rule: 'À partir d\'avril 2026, TOUJOURS mentionner la BIG PROMO: barrique + transport tout compris à 150€. Mentionner dès qu\'un client parle de barrique (achat ou envoi), après avoir donné un prix, ou quand il demande les promotions. Dire: "Et bonne nouvelle: à partir d\'avril, promo à 150€ tout compris (barrique + transport maritime)! 🎉"',
      priority: 'high',
      category: 'consistency'
    }
  ];

  // Initialize with default rules if empty
  React.useEffect(() => {
    if (!rules || rules?.length === 0) {
      onChange(defaultRules);
    }
  }, []);

  const handleAdd = () => {
    if (newRule?.title?.trim() && newRule?.rule?.trim()) {
      onChange([...(rules || []), { ...newRule, id: Date.now() }]);
      setNewRule({ title: '', rule: '', priority: 'high', category: 'accuracy' });
      setIsAdding(false);
    }
  };

  const handleEdit = (index, field, value) => {
    const updated = [...(rules || [])];
    updated[index] = { ...updated?.[index], [field]: value };
    onChange(updated);
  };

  const handleDelete = (index) => {
    const updated = (rules || [])?.filter((_, i) => i !== index);
    onChange(updated);
  };

  const currentRules = rules || defaultRules;

  // Sort by priority
  const sortedRules = [...currentRules]?.sort((a, b) => {
    const priorityOrder = { critical: 0, high: 1, medium: 2 };
    return priorityOrder?.[a?.priority] - priorityOrder?.[b?.priority];
  });

  const getCategoryColor = (category) => {
    return ruleCategories?.find(c => c?.value === category)?.color || 'bg-gray-100 text-gray-800';
  };

  const getPriorityInfo = (priority) => {
    return priorities?.find(p => p?.value === priority) || priorities?.[1];
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Shield className="text-green-600" size={24} />
          <h2 className="text-xl font-semibold text-gray-900">Règles de Cohérence</h2>
        </div>
        <button
          onClick={() => setIsAdding(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <Plus size={18} />
          <span>Ajouter Règle</span>
        </button>
      </div>
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-sm text-yellow-800 font-medium">Importance Critique</p>
            <p className="text-sm text-yellow-700 mt-1">
              Ces règles garantissent que Fatou donne des réponses précises, cohérentes et conformes à vos politiques. Elles sont intégrées dans le système prompt de l'IA et appliquées à chaque réponse.
            </p>
          </div>
        </div>
      </div>
      {/* Add New Rule Form */}
      {isAdding && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <h3 className="font-medium text-gray-900 mb-3">Nouvelle Règle</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titre de la Règle
              </label>
              <input
                type="text"
                value={newRule?.title}
                onChange={(e) => setNewRule({ ...newRule, title: e?.target?.value })}
                placeholder="Ex: Vérification des informations avant réponse"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Catégorie
                </label>
                <select
                  value={newRule?.category}
                  onChange={(e) => setNewRule({ ...newRule, category: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  {ruleCategories?.map(cat => (
                    <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priorité
                </label>
                <select
                  value={newRule?.priority}
                  onChange={(e) => setNewRule({ ...newRule, priority: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  {priorities?.map(pri => (
                    <option key={pri?.value} value={pri?.value}>{pri?.icon} {pri?.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description de la Règle
              </label>
              <textarea
                value={newRule?.rule}
                onChange={(e) => setNewRule({ ...newRule, rule: e?.target?.value })}
                placeholder="Décrivez la règle en détail. Utilisez un langage clair et direct."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleAdd}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Save size={16} />
                <span>Enregistrer</span>
              </button>
              <button
                onClick={() => {
                  setIsAdding(false);
                  setNewRule({ title: '', rule: '', priority: 'high', category: 'accuracy' });
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                <X size={16} />
                <span>Annuler</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Rules List */}
      <div className="space-y-3">
        {sortedRules?.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
            <Shield size={48} className="mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600">Aucune règle configurée</p>
            <p className="text-sm text-gray-500 mt-1">Cliquez sur "Ajouter Règle" pour commencer</p>
          </div>
        ) : (
          sortedRules?.map((rule, index) => {
            const priorityInfo = getPriorityInfo(rule?.priority);
            const actualIndex = currentRules?.findIndex(r => r?.id === rule?.id);
            const isEditing = editingIndex === actualIndex;
            
            return (
              <div 
                key={rule?.id || index} 
                className={`border rounded-lg p-4 ${
                  rule?.priority === 'critical' ?'bg-red-50 border-red-200' :'bg-white border-gray-200'
                }`}
              >
                {isEditing ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Titre de la Règle
                      </label>
                      <input
                        type="text"
                        value={rule?.title}
                        onChange={(e) => handleEdit(actualIndex, 'title', e?.target?.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Catégorie
                        </label>
                        <select
                          value={rule?.category}
                          onChange={(e) => handleEdit(actualIndex, 'category', e?.target?.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                          {ruleCategories?.map(cat => (
                            <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Priorité
                        </label>
                        <select
                          value={rule?.priority}
                          onChange={(e) => handleEdit(actualIndex, 'priority', e?.target?.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                          {priorities?.map(pri => (
                            <option key={pri?.value} value={pri?.value}>{pri?.icon} {pri?.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description de la Règle
                      </label>
                      <textarea
                        value={rule?.rule}
                        onChange={(e) => handleEdit(actualIndex, 'rule', e?.target?.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setEditingIndex(null)}
                        className="flex items-center space-x-2 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                      >
                        <Save size={14} />
                        <span>Enregistrer</span>
                      </button>
                      <button
                        onClick={() => setEditingIndex(null)}
                        className="flex items-center space-x-2 px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                      >
                        <X size={14} />
                        <span>Annuler</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className={`text-sm font-medium ${priorityInfo?.icon}`}>
                            {priorityInfo?.icon} {priorityInfo?.label}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(rule?.category)}`}>
                            {ruleCategories?.find(c => c?.value === rule?.category)?.label}
                          </span>
                        </div>
                        <h3 className="font-semibold text-gray-900 mb-2">{rule?.title}</h3>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{rule?.rule}</p>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => setEditingIndex(actualIndex)}
                          className="text-blue-600 hover:text-blue-700 transition-colors"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(actualIndex)}
                          className="text-red-600 hover:text-red-700 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
        <p className="text-sm text-blue-800">
          <strong>💡 Conseil:</strong> Les règles critiques (🔴) sont appliquées en priorité absolue. Elles garantissent la précision et la sécurité des réponses de Fatou. Les règles de haute priorité (🟠) assurent la cohérence, et les règles moyennes (🟡) améliorent l'expérience utilisateur.
        </p>
      </div>
    </div>
  );
}